import { Reveal, Eyebrow } from "./Reveal";
import {
  Clock,
  Headset,
  BrainCircuit,
  DatabaseZap,
  SlidersHorizontal,
  BarChart3,
} from "lucide-react";

const vanImg =
  "https://images.unsplash.com/photo-1696592783645-33e966c4c274?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzZXJ2aWNlJTIwdmFuJTIwdHJ1Y2slMjBmbGVldCUyMG5pZ2h0JTIwY2l0eXxlbnwwfHx8fDE3OTExMjUxMTZ8MA&ixlib=rb-4.1.0&q=85";

const Card = ({ icon: Icon, title, body, className = "", testid, children }) => (
  <div
    data-testid={testid}
    className={`group relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#12161F] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#F25F22]/40 ${className}`}
  >
    <div className="relative z-10">
      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#F25F22]/30 bg-[#F25F22]/10">
        <Icon className="h-5 w-5 text-[#F25F22]" />
      </div>
      <h3 className="mt-5 font-display text-lg font-semibold text-slate-50">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{body}</p>
      {children}
    </div>
  </div>
);

export default function Bento() {
  return (
    <section id="features" data-testid="bento-section" className="border-t border-white/[0.06] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Capabilities</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            A dispatcher&apos;s toolkit, without the dispatcher&apos;s salary
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-4 lg:grid-cols-6">
          <Reveal className="lg:col-span-4" delay={0}>
            <Card
              icon={Clock}
              title="24/7 after-hours automated SMS"
              body="Nights, weekends, holidays — the 9 PM no-cool call gets answered in seconds and queued for your morning crew."
              testid="bento-card-afterhours"
              className="h-full"
            >
              <div className="relative mt-6 h-32 overflow-hidden rounded-lg border border-white/[0.08]">
                <img
                  src={vanImg}
                  alt="Service van at night"
                  data-testid="bento-van-image"
                  className="h-full w-full object-cover contrast-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#12161F] via-transparent to-transparent" />
                <span className="absolute bottom-3 right-3 rounded-full border border-[#F25F22]/40 bg-[#0B0D10]/80 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#F25F22] backdrop-blur">
                  9:04 PM · Answered in 7s
                </span>
              </div>
            </Card>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0.08}>
            <Card
              icon={Headset}
              title="Human dispatcher backup"
              body="Urgent, confusing, or sensitive conversations route to a real dispatcher or your on-call team member."
              testid="bento-card-human"
              className="h-full"
            />
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0}>
            <Card
              icon={BrainCircuit}
              title="Smart qualification flow"
              body="Repair, maintenance, emergency, or replacement — sorted with address, system age, and urgency captured."
              testid="bento-card-qualification"
              className="h-full"
            />
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.08}>
            <Card
              icon={DatabaseZap}
              title="One dashboard for every lead"
              body="Recovered calls, qualified leads, bookings, and full transcripts — your office sees everything in one place. No double entry, no sticky notes."
              testid="bento-card-crm"
              className="h-full"
            >
              <div className="mt-5 flex flex-wrap gap-2">
                {["Missed calls", "Web leads", "Estimates", "Bookings"].map(
                  (c) => (
                    <span
                      key={c}
                      className="rounded-full border border-white/[0.1] bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-slate-300"
                    >
                      {c}
                    </span>
                  )
                )}
              </div>
            </Card>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0}>
            <Card
              icon={SlidersHorizontal}
              title="Custom scripting rules"
              body="Your tone, your pricing guardrails, your escalation thresholds — tuned to how your shop actually runs."
              testid="bento-card-scripting"
              className="h-full"
            />
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.08}>
            <Card
              icon={BarChart3}
              title="Live contractor analytics"
              body="Response times, recovery rate, and booked value — visible at a glance, not buried in a report."
              testid="bento-card-analytics"
              className="h-full"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
